import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  Wrench, PlusCircle, Camera, ImagePlus, ScanLine, Loader2, Check, X, Trash2,
  ClipboardList, Receipt, Sparkles, AlertTriangle, CheckCircle2, ChevronRight, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";
import ReceiptViewer, { ReceiptEmpty } from "@/components/ReceiptViewer";

const INR = (n) => `₹${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const URGENCY = {
  low: { color: "text-[#A3A3A3]", label: "LOW" },
  normal: { color: "text-[#3B82F6]", label: "NORMAL" },
  high: { color: "text-[#EF4444]", label: "HIGH" },
};
const STATUS_STYLES = {
  pending: "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]",
  approved: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]",
  rejected: "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]",
  completed: "border-[#3B82F6]/40 bg-[#3B82F6]/10 text-[#3B82F6]",
};

export default function Service() {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_admin);
  const [tab, setTab] = useState("requests");
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <p className="ft-overline mb-2">SERVICE & REPAIR</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">Workshop</h1>
        <p className="mt-3 text-[#A3A3A3] max-w-2xl">
          {isAdmin
            ? "Approve driver requests, upload workshop bills (with AI), and track service spend per vehicle."
            : "Raise routine service or repair requests, upload workshop bills — even snap them and let AI read the details."}
        </p>
      </div>

      <div className="flex gap-1 border-b border-[#262626] overflow-x-auto">
        <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} testid="service-tab-requests">
          <ClipboardList size={14} /> Requests
        </TabBtn>
        <TabBtn active={tab === "bills"} onClick={() => setTab("bills")} testid="service-tab-bills">
          <Receipt size={14} /> Bills
        </TabBtn>
      </div>

      {tab === "requests" && <RequestsPanel vehicles={vehicles} isAdmin={isAdmin} user={user} />}
      {tab === "bills" && <BillsPanel vehicles={vehicles} isAdmin={isAdmin} />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-3 text-sm font-semibold inline-flex items-center gap-2 transition-colors duration-200 ${
        active ? "text-white border-b-2 border-[#FF5A1F] -mb-px" : "text-[#A3A3A3] hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

/* ============ Requests ============ */
function RequestsPanel({ vehicles, isAdmin, user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/service-requests", { params });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async (item, kind) => {
    const note = kind === "reject" ? window.prompt(`Reason for rejecting "${item.title}"? (optional)`) : "";
    try {
      await api.post(`/service-requests/${item.id}/${kind}`, { admin_note: note || null });
      toast.success(kind === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const complete = async (item) => {
    try {
      await api.post(`/service-requests/${item.id}/complete`);
      toast.success("Marked completed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete request "${item.title}"?`)) return;
    try {
      await api.delete(`/service-requests/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 flex-wrap" data-testid="requests-status-filter">
          {["", "pending", "approved", "rejected", "completed"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
                statusFilter === s ? "bg-[#FF5A1F] text-black border-[#FF5A1F]" : "border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#FF5A1F]"
              }`}
              data-testid={`req-filter-${s || "all"}`}
            >
              {s || "All"}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="bg-[#FF5A1F] text-black px-4 py-2 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors"
          data-testid="new-request-toggle"
        >
          {showForm ? <X size={14} /> : <PlusCircle size={14} />} {showForm ? "Cancel" : "New Request"}
        </button>
      </div>

      {showForm && <NewRequestForm vehicles={vehicles} onSaved={() => { setShowForm(false); load(); }} />}

      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <div className="ft-card p-12 text-center text-[#737373]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="ft-card p-12 text-center text-[#A3A3A3]">No service requests {statusFilter && `in ${statusFilter}`}.</div>
        ) : (
          items.map((r) => {
            const veh = vehicles.find((v) => v.id === r.vehicle_id);
            const isRoutine = r.kind === "routine";
            return (
              <div key={r.id} className="ft-card p-5" data-testid={`req-card-${r.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${isRoutine ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"}`} data-testid={`req-kind-${r.id}`}>
                        {r.kind || "repair"}
                      </span>
                      <h4 className="font-display text-xl font-bold tracking-tight">{r.title}</h4>
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${STATUS_STYLES[r.status] || ""}`} data-testid={`req-status-${r.id}`}>
                        {r.status}
                      </span>
                    </div>
                    <div className="text-[#A3A3A3] text-sm mt-1 font-mono">
                      {veh ? <Link className="text-[#FF5A1F] hover:underline" to={`/vehicles/${veh.id}`}>{veh.number}</Link> : "—"}
                      {" · "}by {r.user_name} · {r.created_at?.slice(0, 10)}
                    </div>
                    {!isRoutine && r.reason && (
                      <p className="mt-3 text-white text-sm"><span className="text-[#737373]">Reason:</span> {r.reason}</p>
                    )}
                    {r.admin_note && (
                      <p className="mt-2 text-xs border-l-2 border-[#FF5A1F] pl-2 text-[#A3A3A3]">
                        Admin note: {r.admin_note}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="ft-overline">ODOMETER</p>
                    <p className="ft-metric text-2xl">{r.odometer ? `${Number(r.odometer).toLocaleString()} km` : "—"}</p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-[#262626] flex-wrap">
                  {isAdmin && r.status === "pending" && (
                    <>
                      <button onClick={() => decide(r, "approve")} className="border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981] hover:text-black px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`req-approve-${r.id}`}>
                        <Check size={14} /> Approve
                      </button>
                      <button onClick={() => decide(r, "reject")} className="border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444] hover:text-white px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`req-reject-${r.id}`}>
                        <X size={14} /> Reject
                      </button>
                    </>
                  )}
                  {r.status === "approved" && (r.user_id === user?.id || isAdmin) && (
                    <button onClick={() => complete(r)} className="border border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6] hover:text-white px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`req-complete-${r.id}`}>
                      <CheckCircle2 size={14} /> Mark Completed
                    </button>
                  )}
                  {(isAdmin || (r.user_id === user?.id && r.status === "pending")) && (
                    <button onClick={() => remove(r)} className="text-[#EF4444] hover:bg-[#EF4444] hover:text-white p-2 transition-colors" data-testid={`req-delete-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function NewRequestForm({ vehicles, onSaved }) {
  const [form, setForm] = useState({
    kind: "routine",
    vehicle_id: vehicles[0]?.id || "",
    odometer: "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.vehicle_id) return setErr("Select a vehicle");
    if (!form.odometer) return setErr("Enter the odometer reading");
    if (form.kind === "repair" && !form.reason.trim()) return setErr("Reason for repair is required");
    setSaving(true);
    try {
      await api.post("/service-requests", {
        kind: form.kind,
        vehicle_id: form.vehicle_id,
        odometer: Number(form.odometer),
        reason: form.kind === "repair" ? form.reason.trim() : null,
      });
      toast.success("Request submitted — awaiting admin approval");
      onSaved();
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="ft-card p-6 space-y-4" data-testid="new-request-form">
      <p className="ft-overline">RAISE SERVICE REQUEST</p>

      <div>
        <label className="ft-overline block mb-2">Type</label>
        <select
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
          className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]"
          data-testid="req-kind"
        >
          <option value="routine">Routine Service</option>
          <option value="repair">Repair</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="ft-overline block mb-2">Vehicle</label>
          <select
            value={form.vehicle_id}
            onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]"
            data-testid="req-vehicle"
          >
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{`${v.number} · ${v.name}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="ft-overline block mb-2">Odometer (km)</label>
          <input
            type="number"
            step="0.01"
            value={form.odometer}
            onChange={(e) => setForm({ ...form, odometer: e.target.value })}
            placeholder="e.g. 26000"
            className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white text-xl focus:outline-none focus:border-[#FF5A1F]"
            data-testid="req-odometer"
          />
        </div>
      </div>

      {form.kind === "repair" && (
        <div>
          <label className="ft-overline block mb-2">Reason for Repair</label>
          <textarea
            rows={3}
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="e.g. Squeaking noise while braking"
            className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]"
            data-testid="req-reason"
          />
        </div>
      )}

      {err && (
        <div className="text-[#EF4444] text-sm border border-[#EF4444]/30 bg-[#EF4444]/10 p-3" data-testid="req-error">
          {err}
        </div>
      )}

      <p className="text-xs text-[#737373]">
        {form.kind === "routine"
          ? "Routine service — vehicle + odometer only. Admin will approve."
          : "Repair — vehicle, odometer and reason. Admin will approve."}
      </p>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="bg-[#FF5A1F] text-black px-6 py-3 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors disabled:opacity-60" data-testid="req-submit">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />} Submit for approval
        </button>
      </div>
    </form>
  );
}

/* ============ Bills ============ */
function BillsPanel({ vehicles, isAdmin }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/service-bills");
      setBills(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (b) => {
    if (!window.confirm("Delete this bill?")) return;
    try {
      await api.delete(`/service-bills/${b.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="ft-overline">SERVICE BILLS</p>
          <p className="text-[#A3A3A3] text-sm mt-1">Upload photos of workshop bills · we OCR them or you fill manually.</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)} className="bg-[#FF5A1F] text-black px-4 py-2 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors" data-testid="new-bill-toggle">
          {showForm ? <X size={14} /> : <PlusCircle size={14} />} {showForm ? "Cancel" : "New Bill"}
        </button>
      </div>

      {showForm && <NewBillForm vehicles={vehicles} onSaved={() => { setShowForm(false); load(); }} />}

      <div className="ft-card p-0 overflow-auto">
        {loading ? (
          <div className="p-12 text-center text-[#737373]">Loading…</div>
        ) : bills.length === 0 ? (
          <div className="p-12 text-center text-[#A3A3A3]">No bills yet.</div>
        ) : (
          <table className="w-full" data-testid="bills-table">
            <thead>
              <tr>
                {["Date", "Vehicle", "Vendor", "Items", "Total", "Bill", ""].map((h) => (
                  <th key={h} className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {bills.map((b) => {
                const v = vehicles.find((x) => x.id === b.vehicle_id);
                return (
                  <tr key={b.id} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors" data-testid={`bill-row-${b.id}`}>
                    <td className="py-3 px-4 text-[#A3A3A3]">{(b.date || "").slice(0, 10)}</td>
                    <td className="py-3 px-4">{v ? <Link to={`/vehicles/${v.id}`} className="text-[#FF5A1F] hover:underline">{v.number}</Link> : "—"}</td>
                    <td className="py-3 px-4 text-[#A3A3A3]">{b.vendor || "—"}</td>
                    <td className="py-3 px-4 text-[#A3A3A3]">{b.items?.length || 0} item{(b.items?.length || 0) === 1 ? "" : "s"}</td>
                    <td className="py-3 px-4 text-white font-bold">{INR(b.total_amount)}</td>
                    <td className="py-3 px-4">{b.bill_path ? <ReceiptViewer path={b.bill_path} /> : <ReceiptEmpty />}</td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => remove(b)} className="text-[#EF4444] hover:bg-[#EF4444] hover:text-white p-2 transition-colors" data-testid={`bill-delete-${b.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ============ Logs (routine + repair quick log history) ============ */
function LogsPanel({ vehicles, reloadKey }) {
  const [logs, setLogs] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (vehicleId) params.vehicle_id = vehicleId;
      if (kindFilter) params.kind = kindFilter;
      const { data } = await api.get("/service-logs", { params });
      setLogs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicleId, kindFilter, reloadKey]);

  const remove = async (l) => {
    if (!window.confirm("Delete this log entry?")) return;
    try {
      await api.delete(`/service-logs/${l.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const vehById = Object.fromEntries(vehicles.map((v) => [v.id, v]));

  return (
    <div className="space-y-6">
      <div className="flex items-center flex-wrap gap-3">
        <span className="ft-overline">FILTER</span>
        <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid="logs-vehicle-filter">
          <option value="">All vehicles</option>
          {vehicles.map((v) => (<option key={v.id} value={v.id}>{`${v.number} · ${v.name}`}</option>))}
        </select>
        <div className="flex gap-1">
          {[{ k: "", l: "All" }, { k: "routine", l: "Routine" }, { k: "repair", l: "Repair" }].map((o) => (
            <button
              key={o.k || "all"}
              onClick={() => setKindFilter(o.k)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${kindFilter === o.k ? "bg-[#FF5A1F] text-black border-[#FF5A1F]" : "border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#FF5A1F]"}`}
              data-testid={`logs-kind-${o.k || "all"}`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <span className="ml-auto text-[#737373] text-xs font-mono">{logs.length} entries</span>
      </div>

      <div className="ft-card p-0 overflow-auto">
        {loading ? (<div className="p-12 text-center text-[#737373]">Loading…</div>)
         : logs.length === 0 ? (<div className="p-12 text-center text-[#A3A3A3]">No logs yet. Use "Log Routine" or "Log Repair" above.</div>)
         : (
          <table className="w-full" data-testid="logs-table">
            <thead>
              <tr>
                {["Date", "Kind", "Vehicle", "Odometer", "Title", ""].map((h) => (
                  <th key={h} className="text-left py-4 px-3 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {logs.map((l) => {
                const v = vehById[l.vehicle_id];
                return (
                  <tr key={l.id} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors" data-testid={`log-row-${l.id}`}>
                    <td className="py-3 px-3 text-[#A3A3A3]">{(l.date || "").slice(0, 10)}</td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${l.kind === "routine" ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"}`}>
                        {l.kind}
                      </span>
                    </td>
                    <td className="py-3 px-3">{v ? <Link to={`/vehicles/${v.id}`} className="text-[#FF5A1F] hover:underline">{v.number}</Link> : "—"}</td>
                    <td className="py-3 px-3 text-right text-white">{Number(l.odometer).toLocaleString()} km</td>
                    <td className="py-3 px-3 text-[#A3A3A3]">{l.title || "—"}</td>
                    <td className="py-3 px-3 text-right">
                      <button onClick={() => remove(l)} className="text-[#EF4444] hover:bg-[#EF4444] hover:text-white p-2 transition-colors" data-testid={`log-delete-${l.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function NewBillForm({ vehicles, onSaved }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [availableRequests, setAvailableRequests] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: vehicles[0]?.id || "",
    date: new Date().toISOString().slice(0, 10),
    total_amount: "",
    vendor: "",
    remarks: "",
    items: [],
    service_request_id: "",
  });

  // Load requests eligible for matching for the currently selected vehicle
  useEffect(() => {
    if (!form.vehicle_id) return setAvailableRequests([]);
    api.get("/service-requests", { params: { vehicle_id: form.vehicle_id } })
      .then((r) => setAvailableRequests(r.data.filter((x) => ["approved", "completed"].includes(x.status))))
      .catch(() => setAvailableRequests([]));
  }, [form.vehicle_id]);

  const onPickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const runOcr = async () => {
    if (!file) return toast.error("Pick a bill photo first");
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/service-bills/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const patch = {};
      if (data.date) patch.date = data.date.slice(0, 10);
      if (data.total_amount) patch.total_amount = String(data.total_amount);
      if (data.vendor) patch.vendor = data.vendor;
      if (data.remarks) patch.remarks = data.remarks;
      if (data.items?.length) patch.items = data.items;
      // Match vehicle by number if extracted
      if (data.vehicle_number) {
        const cleaned = data.vehicle_number.replace(/\s/g, "").toUpperCase();
        const match = vehicles.find((v) => v.number.replace(/\s/g, "").toUpperCase() === cleaned);
        if (match) patch.vehicle_id = match.id;
      }
      setForm((f) => ({ ...f, ...patch }));
      toast.success("Bill scanned · verify below");
    } catch (e) { toast.error(formatApiError(e)); } finally { setScanning(false); }
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), { description: "", cost: 0 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const itemsSum = (form.items || []).reduce((s, it) => s + (Number(it.cost) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Select a vehicle");
    if (!form.total_amount) return toast.error("Enter the total amount");
    setSaving(true);
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        date: form.date,
        total_amount: Number(form.total_amount),
        vendor: form.vendor || null,
        remarks: form.remarks || null,
        service_request_id: form.service_request_id || null,
        items: (form.items || []).map((it) => ({ description: it.description, cost: Number(it.cost) || 0 })).filter((it) => it.description),
      };
      const { data: created } = await api.post("/service-bills", payload);
      if (file && created?.id) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/service-bills/${created.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (up) {
          toast.error("Bill saved but photo upload failed");
        }
      }
      toast.success("Bill saved");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="ft-card p-6 space-y-5" data-testid="new-bill-form">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: capture */}
        <div className="lg:col-span-2 space-y-3">
          <p className="ft-overline">1 · BILL PHOTO</p>
          {!previewUrl ? (
            <>
              <button type="button" onClick={() => cameraRef.current?.click()} className="w-full bg-[#FF5A1F] text-black px-4 py-5 font-bold inline-flex items-center justify-center gap-3 hover:bg-[#E04F1A] transition-colors" data-testid="bill-take-photo">
                <Camera size={22} /> Take Photo
              </button>
              <button type="button" onClick={() => galleryRef.current?.click()} className="w-full border border-[#262626] text-white px-4 py-5 font-bold inline-flex items-center justify-center gap-3 hover:border-[#FF5A1F] hover:bg-[#1A1A1A] transition-colors" data-testid="bill-choose-photo">
                <ImagePlus size={22} /> Choose from Photos
              </button>
              <p className="text-xs text-[#737373] text-center flex items-center justify-center gap-2">
                <Sparkles size={12} className="text-[#FF5A1F]" /> Optional — you can add details manually.
              </p>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="bill-camera-input" />
              <input ref={galleryRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="bill-gallery-input" />
            </>
          ) : (
            <>
              <div className="border border-[#262626] bg-[#0A0A0A] p-2">
                <img src={previewUrl} alt="Bill preview" className="max-h-64 mx-auto" data-testid="bill-preview" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={runOcr} disabled={scanning} className="flex-1 bg-[#FF5A1F] text-black px-4 py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-[#E04F1A] transition-colors disabled:opacity-60" data-testid="bill-scan">
                  {scanning ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
                  {scanning ? "Scanning…" : "Scan with AI"}
                </button>
                <button type="button" onClick={() => { setFile(null); setPreviewUrl(null); if (cameraRef.current) cameraRef.current.value = ""; if (galleryRef.current) galleryRef.current.value = ""; }} className="border border-[#262626] px-4 py-3 hover:bg-[#1A1A1A] text-[#A3A3A3] hover:text-white transition-colors" data-testid="bill-clear">
                  <X size={16} />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right: form */}
        <div className="lg:col-span-3 space-y-4">
          <p className="ft-overline">2 · BILL DETAILS</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="ft-overline block mb-2">Vehicle</label>
              <select required value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid="bill-vehicle">
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{`${v.number} · ${v.name}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="ft-overline block mb-2">Date</label>
              <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid="bill-date" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="ft-overline block mb-2">Total Amount (₹)</label>
              <input required type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white text-xl focus:outline-none focus:border-[#FF5A1F]" data-testid="bill-total" />
              {itemsSum > 0 && (
                <p className="text-xs text-[#737373] mt-1">Sum of items: {INR(itemsSum)}</p>
              )}
            </div>
            <div>
              <label className="ft-overline block mb-2">Vendor / Garage</label>
              <input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. Chandkheda Auto Care" className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid="bill-vendor" />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="ft-overline">Line Items (optional)</label>
              <button type="button" onClick={addItem} className="text-xs text-[#FF5A1F] hover:underline inline-flex items-center gap-1" data-testid="bill-add-item">
                <PlusCircle size={12} /> Add item
              </button>
            </div>
            {(form.items || []).length === 0 ? (
              <p className="text-xs text-[#737373]">No items yet. AI scan will populate these from the bill.</p>
            ) : (
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`bill-item-${i}`}>
                    <input value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} placeholder="Description" className="flex-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white text-sm focus:outline-none focus:border-[#FF5A1F]" />
                    <input type="number" step="0.01" value={it.cost} onChange={(e) => setItem(i, { cost: e.target.value })} placeholder="Cost" className="w-28 bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white text-sm text-right focus:outline-none focus:border-[#FF5A1F]" />
                    <button type="button" onClick={() => removeItem(i)} className="text-[#EF4444] hover:bg-[#EF4444] hover:text-white p-2"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="ft-overline block mb-2">Remarks</label>
            <textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]" data-testid="bill-remarks" />
          </div>

          <div>
            <label className="ft-overline block mb-2">Match to service request (optional)</label>
            <select
              value={form.service_request_id}
              onChange={(e) => setForm({ ...form, service_request_id: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F]"
              data-testid="bill-match-request"
            >
              <option value="">— No match —</option>
              {availableRequests.map((r) => (
                <option key={r.id} value={r.id}>{`${r.title} · ${r.status} · est ₹${r.estimated_cost}`}</option>
              ))}
            </select>
            {availableRequests.length === 0 && form.vehicle_id && (
              <p className="text-xs text-[#737373] mt-1">No approved/completed requests for this vehicle to match.</p>
            )}
          </div>

          <button type="submit" disabled={saving} className="w-full bg-[#FF5A1F] text-black px-6 py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-[#E04F1A] transition-colors disabled:opacity-60" data-testid="bill-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save Bill
          </button>
        </div>
      </div>
    </form>
  );
}
